'use client';

import { measure } from '@/components/Analytics/Analytics';
import TextButton from '@/components/Button/TextButton';
import EmojiText from '@/components/EmojiText/EmojiText';
import {
    mdiAlertCircle,
    mdiArrowRightCircle,
    mdiCloseCircle,
    mdiLock,
} from '@mdi/js';
import Icon from '@mdi/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';

const MediaMachinePermissionManager = ({
    canSkip,
    onNoPermission,
    children,
}: {
    canSkip: boolean;
    onNoPermission: () => void;
    children: ReactNode;
}) => {
    const [canContinue, setCanContinue] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [hasSkipped, setHasSkipped] = useState(false);

    useEffect(() => {
        if (navigator.permissions) {
            navigator.permissions
                .query({ name: 'camera' })
                .then((permissionStatus) => {
                    if (permissionStatus.state === 'granted') {
                        measure(
                            'media_machine_camera_permission_granted_automatically',
                        );
                        setCanContinue(true);
                    } else {
                        setCanContinue(false);
                    }
                });
        } else {
            setCanContinue(true);
        }
    }, [setCanContinue]);

    const getPermission = useCallback(() => {
        navigator.mediaDevices
            .getUserMedia({ video: true, audio: true })
            .then(() => {
                measure('media_machine_camera_permission_granted_manually');
                setCanContinue(true);
            })
            .catch(() => {
                measure('media_machine_camera_permission_blocked_manually');
                setCanContinue(false);
                setHasError(true);
            });
    }, [setCanContinue, setHasError]);

    return (
        <div>
            {!canContinue && (
                <div
                    role="alert"
                    aria-label="Cameratoestemming"
                    className="grid gap-4 max-w-md p-4 font-bold text-center bg-white rounded-xl shadow-sm"
                >
                    <span
                        className="relative mx-auto text-7xl -mb-16 -translate-y-16 -rotate-6"
                        aria-hidden
                    >
                        <EmojiText>📸</EmojiText>
                        <span className="absolute text-3xl bottom-0 -right-1 rotate-12">
                            <EmojiText>🔒</EmojiText>
                        </span>
                    </span>
                    <p className="px-3 py-1 text-balance bg-primary-50/30 text-primary-700 rounded-lg">
                        <Icon className="inline" path={mdiLock} size={1} /> Jouw
                        foto&apos;s blijven alleen op jouw apparaat
                    </p>
                    {hasError && (
                        <p className="px-3 py-1 text-balance bg-secondary-50 text-secondary-800 rounded-lg">
                            <Icon
                                className="inline"
                                path={mdiAlertCircle}
                                size={1}
                            />{' '}
                            Helaas kan de camera niet gestart worden. Wil je dit
                            wel graag doen? Vraag dan iemand om hulp.
                        </p>
                    )}
                    <p>
                        Voor dit onderdeel heb je je camera nodig. Hiervoor moet
                        je misschien toestemming geven.
                    </p>
                    <TextButton
                        icon={mdiArrowRightCircle}
                        onClick={getPermission}
                        isSmall={hasSkipped}
                    >
                        {hasError ? 'Opnieuw proberen' : 'Oké!'}
                    </TextButton>
                    {canSkip && !hasSkipped && (
                        <TextButton
                            isSmall
                            isPrimary={false}
                            icon={mdiCloseCircle}
                            onClick={() => {
                                measure(
                                    'media_machine_camera_permission_skipped',
                                );
                                setCanContinue(false);
                                setHasError(true);
                                setHasSkipped(true);
                                onNoPermission();
                            }}
                        >
                            Overslaan
                        </TextButton>
                    )}
                </div>
            )}
            <div
                aria-hidden={!canContinue}
                // @ts-expect-error inert not in types yet
                inert={!canContinue ? 'true' : undefined}
                className={
                    canContinue
                        ? ''
                        : 'absolute -top-[500vh] -left-[500vw] scale-0'
                }
            >
                {children}
            </div>
        </div>
    );
};

export default MediaMachinePermissionManager;
